import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "../api/api";
import { setConfig } from "../reduxtoolkit/userconfigslice";
import { getPracticeId } from "../config/practice";
import dayjs from "dayjs";
import "./orders.css";
import { chipFor, OSA_FAX_HINT_TITLE } from "./sleepOrderFax";

/* Shared by every order tab except Refills: the Today / Scheduled /
   Prescriptions / Notes cards and the universal-search suggestions. The tabs
   keep their own data and popups; this only renders them.

   Today and Scheduled double as the target for new orders: clicking a card
   makes it the one new orders go into (selectedCard "f" / "t"), as before. */

export const OrderIcons = {
  labs: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 3.5h6M10 3.5v6.2L4.8 18.4A1.4 1.4 0 0 0 6 20.5h12a1.4 1.4 0 0 0 1.2-2.1L14 9.7V3.5" />
      <path d="M7.5 14.5h9" />
    </svg>
  ),
  procedures: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="3 12 7.5 12 10 6.5 14 17.5 16.5 12 21 12" />
    </svg>
  ),
  injections: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m18 2.5 3.5 3.5M16.5 4l3.5 3.5M19 6l-9.5 9.5-3-3L16 3" />
      <path d="m7.5 13.5-4 4L3 21l3.5-.5 4-4M11 8l2 2M9 10l2 2" />
    </svg>
  ),
  refills: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m10.5 20.5-6.9-6.9a4.9 4.9 0 0 1 6.9-6.9l6.9 6.9a4.9 4.9 0 0 1-6.9 6.9Z" />
      <path d="m7 10 7 7" />
    </svg>
  ),
  referrals: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 4h6v6M20 4l-9 9" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  ),
  records: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8L14 3.5Z" />
      <path d="M14 3.5V8h4.5M9 12.5h6M9 16h4" />
    </svg>
  ),
  vitamins: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="8.5" width="18" height="7" rx="3.5" transform="rotate(-35 12 12)" />
      <path d="m9.6 8.6 4.8 6.8" />
    </svg>
  ),
  schedule: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </svg>
  ),
  clipboard: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="4.5" width="14" height="16" rx="2" />
      <path d="M9 4.5V3.5h6v1M9 12l2 2 4-4" />
    </svg>
  ),
  clock: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  ),
  pill: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m10.5 20.5-6.9-6.9a4.9 4.9 0 0 1 6.9-6.9l6.9 6.9a4.9 4.9 0 0 1-6.9 6.9Z" />
      <path d="m7 10 7 7" />
    </svg>
  ),
  pencil: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4Z" />
      <path d="m13.5 6.5 4 4" />
    </svg>
  ),
  plus: (
    <svg fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  check: (
    <svg fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  search: (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </svg>
  ),
  heart: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.6 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10Z" />
    </svg>
  ),
  starOutline: (
    <svg fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.8L12 3.5Z" />
    </svg>
  ),
  star: (
    <svg fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.8L12 3.5Z" />
    </svg>
  ),
};

/* Display label and colour family for an order's `context`. */
export const orderCategory = (context) => {
  const c = String(context || "").toLowerCase();
  if (!c) return { label: "Referrals", key: "referrals" };
  if (c.startsWith("diagnos")) return { label: "Diagnostic test", key: "diagnostic" };
  if (c.startsWith("lab")) return { label: "Labs", key: "labs" };
  if (c.startsWith("procedure")) return { label: "Procedures", key: "procedures" };
  if (c.startsWith("injection")) return { label: "Injections", key: "injections" };
  if (c.startsWith("schedule")) return { label: "Schedule", key: "schedule" };
  if (c.startsWith("referral")) return { label: "Referrals", key: "referrals" };
  if (c.startsWith("mrecord") || c.startsWith("record")) return { label: "Records", key: "records" };
  if (c.startsWith("vitamin")) return { label: "Vitamins", key: "vitamins" };
  return { label: context, key: "other" };
};

const INACTIVE = ["COMPLETED", "CANCELLEDTASK"];
const isInactive = (item) => INACTIVE.includes(String(item?.status).toUpperCase());
const statusRank = (item) => ({ CREATED: 1, COMPLETED: 2, CANCELLEDTASK: 3 }[item.status] || 4);

// Badge count, as before: open items, or every item once all are closed.
const badgeCount = (items) => {
  if (items.length && items.every(isInactive)) return items.length;
  return items.filter((i) => !isInactive(i)).length;
};

const when = (item) => {
  const d = item?.schedule_date ? dayjs(item.schedule_date) : null;
  return d && d.isValid() ? d : null;
};
const formatWhen = (d) =>
  d.format("H:mm") === "0:00" ? d.format("ddd, MMM D") : d.format("ddd, MMM D · h:mm A");

const StatusPill = ({ item }) => {
  if (item.status === "COMPLETED") {
    return (
      <span className="bmor-pill ok">
        {OrderIcons.check}
        Completed
      </span>
    );
  }
  if (item.status === "CANCELLEDTASK") return <span className="bmor-pill off">Cancelled</span>;
  const d = when(item);
  return d ? <span className="bmor-pill">{formatWhen(d)}</span> : null;
};

/* Sleep-study order backup (owner 10-20): where the order PDF for OSA is. `sleepFax` is
   useSleepOrderFax()'s result (orders/sleepOrderFax.js); tabs that do not pass it show no chip. */
export const SleepFaxChip = ({ item, sleepFax }) => {
  if (!sleepFax?.active) return null;
  const entry = sleepFax.entryFor ? sleepFax.entryFor(item) : null;
  const chip = chipFor(entry);
  if (!chip) return null;
  const busy = sleepFax.retrying === entry.key;
  return (
    <span className="bmor-sleepfax" data-testid="sleepfax-chip">
      <span className={`bmor-pill sleepfax ${chip.tone}`} title={chip.title || chip.text}>
        {chip.tone === "ok" && OrderIcons.check}
        {chip.text}
      </span>
      {chip.retry && (
        <button
          type="button"
          className="bmor-sleepfax-retry"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            sleepFax.retry(entry.key);
          }}
        >
          {busy ? "Retrying..." : "Retry"}
        </button>
      )}
    </span>
  );
};

const groupByCategory = (items) => {
  const groups = [];
  items.forEach((item) => {
    const cat = orderCategory(item.context);
    let g = groups.find((x) => x.key === cat.key);
    if (!g) {
      g = { ...cat, items: [] };
      groups.push(g);
    }
    g.items.push(item);
  });
  return groups;
};

const CardHead = ({ icon, title, count, target, children }) => (
  <div className="bmor-card-head">
    <span className="bmor-card-icon">{icon}</span>
    <span className="bmor-card-title">{title}</span>
    <span className="bmor-count">{count}</span>
    {target && <span className="bmor-target-tag">New orders go here</span>}
    <div className="bmor-card-tools" onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

const EditTool = ({ onClick, label }) => (
  <button type="button" className="bmor-tool icon" title={label} aria-label={label} onClick={onClick}>
    {OrderIcons.pencil}
  </button>
);

const OrderCards = ({
  selectedSuggestionList = [],
  referals = [],
  getordersData,
  selectedCard,
  SetSelectedCard,
  onEdit, // (selectedEdit) => void: "f" today, "t" scheduled, "" prescriptions / notes
  onAddRx,
  onAddNote,
  onScheduleProcedure, // opens the procedure scheduler; tabs without one omit it
  scheduledSelectable = true, // Referrals / Records only show it when already targeted
  sleepFax, // optional: useSleepOrderFax() result -> OSA delivery chip on sleep-study orders
}) => {
  const list = selectedSuggestionList || [];
  const todayAll = [...list.filter((d) => d.schedule === "f"), ...(referals || [])];
  const todayShown = [
    ...list.filter((d) => d.schedule === "f" && d.context !== "Tov"),
    ...(referals || []),
  ].sort((a, b) => statusRank(a) - statusRank(b));
  const scheduled = list.filter((d) => d.schedule === "t").sort((a, b) => statusRank(a) - statusRank(b));
  const prescriptions = getordersData?.rx_recipe ?? [];
  const notes = getordersData?.notes ?? [];

  const todayOpen = todayShown.filter((i) => !isInactive(i)).length;
  const todayDone = todayShown.filter((i) => i.status === "COMPLETED").length;
  const todayCancelled = todayShown.filter((i) => i.status === "CANCELLEDTASK").length;

  const scheduledLocked = !scheduledSelectable && selectedCard !== "t";

  return (
    <div className="bmor bmor-grid">
      {/* Today */}
      <section
        className={`bmor-card today pickable${selectedCard === "f" ? " target" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          SetSelectedCard("f");
        }}
      >
        <CardHead
          icon={OrderIcons.clipboard}
          title="Today's orders"
          count={
            todayAll.length && todayAll.every(isInactive)
              ? todayAll.length
              : todayAll.filter((i) => !isInactive(i) && i.context !== "Tov").length
          }
          target={selectedCard === "f"}
        >
          <EditTool label="Edit today's orders" onClick={() => onEdit("f")} />
        </CardHead>
        <div className="bmor-card-body">
          {todayShown.length === 0 ? (
            <div className="bmor-card-empty">No orders for today yet</div>
          ) : (
            groupByCategory(todayShown).map((g) => (
              <div key={g.key} className={`bmor-group cat-${g.key}`}>
                <div className="bmor-group-head">
                  <span className="bmor-section-dot" />
                  {g.label}
                  <span className="bmor-count">{g.items.filter((i) => !isInactive(i)).length}</span>
                </div>
                {g.items.map((item, i) => (
                  <div
                    key={item.ordre_id ?? item.id ?? i}
                    className={`bmor-item${item.status === "COMPLETED" ? " done" : ""}${
                      item.status === "CANCELLEDTASK" ? " cancelled" : ""
                    }`}
                  >
                    <span className="bmor-item-bar" />
                    <div className="bmor-item-main">
                      <div className="bmor-item-name">{item.ordername}</div>
                      <StatusPill item={item} />
                      <SleepFaxChip item={item} sleepFax={sleepFax} />
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
        <div className="bmor-card-foot">
          <span>
            {todayOpen} {todayOpen === 1 ? "order" : "orders"}
          </span>
          <span>
            {[todayDone && `${todayDone} completed`, todayCancelled && `${todayCancelled} cancelled`]
              .filter(Boolean)
              .join(" · ") || (selectedCard === "f" ? "Adding to today" : "Click to add here")}
          </span>
        </div>
      </section>

      {/* Scheduled */}
      <section
        className={`bmor-card scheduled${scheduledLocked ? " locked" : ""}${
          scheduledSelectable ? " pickable" : ""
        }${selectedCard === "t" ? " target" : ""}`}
        onClick={
          scheduledSelectable
            ? (e) => {
                e.stopPropagation();
                SetSelectedCard("t");
              }
            : undefined
        }
      >
        <CardHead
          icon={OrderIcons.clock}
          title="Scheduled"
          count={badgeCount(scheduled)}
          target={selectedCard === "t"}
        >
          {/* No "+ Schedule" button here (owner 10-07: it confused providers).
              Clicking the card still makes Scheduled the target for new orders. */}
          <EditTool label="Edit scheduled orders" onClick={() => onEdit("t")} />
        </CardHead>
        <div className="bmor-card-body">
          {scheduled.length === 0 ? (
            <div className="bmor-card-empty">Nothing scheduled</div>
          ) : (
            scheduled.map((item, i) => {
              const d = when(item);
              const cat = orderCategory(item.context);
              return (
                <div
                  key={item.ordre_id ?? item.id ?? i}
                  className={`bmor-item${item.status === "COMPLETED" ? " done" : ""}${
                    item.status === "CANCELLEDTASK" ? " cancelled" : ""
                  }`}
                >
                  <div className={`bmor-date${d ? "" : " none"}`}>
                    <div className="bmor-date-m">{d ? d.format("MMM").toUpperCase() : "TBD"}</div>
                    <div className="bmor-date-d">{d ? d.format("D") : "—"}</div>
                  </div>
                  <div className="bmor-item-main">
                    <div className="bmor-item-name">{item.ordername}</div>
                    <div className="bmor-item-sub">
                      {[d && formatWhen(d), cat.label, item.schedule_notes].filter(Boolean).join(" · ")}
                    </div>
                    {isInactive(item) && <StatusPill item={item} />}
                    <SleepFaxChip item={item} sleepFax={sleepFax} />
                  </div>
                  {onScheduleProcedure && item.context === "Procedure" && (
                    <button
                      type="button"
                      className="bmor-tool"
                      onClick={(e) => {
                        e.stopPropagation();
                        onScheduleProcedure();
                      }}
                    >
                      {d ? "Reschedule" : "Schedule"}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Prescriptions */}
      <section className="bmor-card rx">
        <CardHead icon={OrderIcons.pill} title="Prescriptions" count={prescriptions.length}>
          <button type="button" className="bmor-tool" onClick={onAddRx}>
            {OrderIcons.plus}
            Add
          </button>
          <EditTool label="Edit prescriptions" onClick={() => onEdit("")} />
        </CardHead>
        <div className="bmor-card-body">
          {prescriptions.length === 0 ? (
            <div className="bmor-card-empty">No active prescriptions</div>
          ) : (
            prescriptions.map((rx, i) => (
              <div key={rx.orderid ?? i} className="bmor-item" style={{ "--cat": "#2e9e62" }}>
                <span className="bmor-item-bar" />
                <div className="bmor-item-main">
                  <div className="bmor-item-name">{rx.medicationname}</div>
                  <div className="bmor-item-sub">
                    {[rx.dosage_qty, rx.dosage_qty_unit, rx.frequency, rx.duration].filter(Boolean).join(" ")}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Notes */}
      <section className="bmor-card notes">
        <CardHead icon={OrderIcons.pencil} title="Notes" count={notes.length}>
          <EditTool label="Edit notes" onClick={() => onEdit("")} />
        </CardHead>
        <div className="bmor-card-body">
          {notes.map((n, i) => (
            <div key={n.id ?? i} className="bmor-note">
              {n.ordername}
            </div>
          ))}
          <button type="button" className="bmor-note-box" onClick={onAddNote}>
            Add a note for these orders…
          </button>
        </div>
      </section>
    </div>
  );
};

/* Which favourites list a search result belongs to: the same user-config keys
   each tab's chip component reads and writes (nav/*buttons.jsx). Diagnostic
   tests have none - the Labs favourites would order them as a plain lab. */
const FAV_KEY_BY_CONTEXT = {
  labs: "fav_labs",
  procedure: "fav_procedures",
  injection: "fav_injections",
  schedule: "fav_schedules",
  referral: "fav_referrals",
  mrecord: "fav_records",
  vitamin: "fav_vitamin",
};
const favKeyFor = (context) => FAV_KEY_BY_CONTEXT[String(context || "").toLowerCase()] || null;

/* Universal-search results: one chip per match, tagged with its category.
   Clicking adds it, as before; the star saves it to (or takes it out of) the
   user's favourites for that category, which the tab's Favourites row shows. */
export const SearchSuggestions = ({ results, onPick, osaHint }) => {
  const practiseid = getPracticeId();
  const userConfig = useSelector((state) => state.userconfig);
  const dispatch = useDispatch();
  const [savingKey, setSavingKey] = useState(null);

  const toggleFavourite = async (result) => {
    const key = favKeyFor(result.context);
    if (!key || savingKey) return;
    const current = userConfig?.config?.[key] || [];
    const updated = current.includes(result.content)
      ? current.filter((x) => x !== result.content)
      : [...current, result.content];
    setSavingKey(result.id);
    try {
      const response = await api.put(`/v1/${practiseid}/user/config`, {
        rowid: userConfig.id,
        [key]: updated,
      });
      if (response.status === 200) {
        dispatch(setConfig({ ...userConfig, config: { ...userConfig.config, [key]: updated } }));
      }
    } catch (error) {
      console.error("Error updating favourites:", error);
    } finally {
      setSavingKey(null);
    }
  };

  if (!results.length) return null;
  return (
    <div className="bmor">
      <div className="bmor-section">
        <span className="bmor-section-title">
          <span className="bmor-section-dot" style={{ background: "#1d2951" }} />
          Search results
        </span>
        <span className="bmor-section-hint">Select to add · star to save as a favourite</span>
      </div>
      <div className="bmor-chips">
        {results.map((result) => {
          const key = favKeyFor(result.context);
          const isFav = Boolean(key && (userConfig?.config?.[key] || []).includes(result.content));
          return (
            <button key={result.id} type="button" className="bmor-chip" onClick={() => onPick(result)}>
              {OrderIcons.plus}
              {result.content}
              <span className="bmor-chip-tag">
                {result.context === "diagnosis" ? "Diagnostic Tests" : result.context}
              </span>
              {osaHint && osaHint(result.content) && (
                <span className="bmor-chip-osa" title={OSA_FAX_HINT_TITLE} data-testid="osa-fax-hint">
                  {osaHint(result.content)}
                </span>
              )}
              {key && (
                <span
                  role="button"
                  tabIndex={0}
                  className={`bmor-chip-fav${isFav ? " on" : ""}`}
                  title={isFav ? "Remove from favourites" : "Add to favourites"}
                  aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
                  aria-pressed={isFav}
                  aria-busy={savingKey === result.id}
                  onClick={(e) => {
                    e.stopPropagation(); // starring must not also order it
                    toggleFavourite(result);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleFavourite(result);
                    }
                  }}
                >
                  {isFav ? OrderIcons.star : OrderIcons.starOutline}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OrderCards;
